import axios from 'axios';
import api from './api';

/**
 * WASTAGE.1 — record stock that is gone but was not sold.
 *
 * POST /admin/venue/:venueId/inventory/waste
 *
 * The same backend service the POS calls, so a write-off recorded here and one
 * recorded at the till produce an identical ledger entry.
 *
 * `reason` is required. A wastage record without one is the unexplained
 * shrinkage number this feature exists to replace, and the backend rejects it.
 *
 * Quantity accepts fractions: wasting one 150ml pour of a 750ml bottle depletes
 * 0.2 of a stock unit. For a RECIPE item the quantity is SERVINGS, and the
 * backend expands it into the components that actually hold stock.
 */

export interface PostWastagePayload {
  locationId: string;
  inventoryItemId: string;
  quantity: number;
  reason: string;
  notes?: string;
  /**
   * MOVEMENT-ATTRIBUTION.1 — who bore the loss.
   *
   * A bottle dropped behind the bar is the venue's. A CORKED one from a shared
   * cellar is not: the wine was faulty on arrival and the venue that opened it
   * did nothing. Nothing in the movement says which, and `reason` is free text
   * rather than an enum, so it cannot be derived — it has to be said.
   *
   * Omitted means VENUE, which is the common case and what this always did.
   */
  bearer?: 'VENUE' | 'COMPANY';
}

export interface WastedComponent {
  inventoryItemId: string;
  quantity: number;
  movementId: string;
}

export interface PostWastageResult {
  action: 'POSTED';
  components: WastedComponent[];
}

export const extractWastageError = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    return (
      (err.response?.data as any)?.error?.message ??
      (err.response?.data as any)?.message ??
      err.message
    );
  }
  return err instanceof Error ? err.message : String(err);
};

const wastageService = {
  postWastage: async (venueId: string, payload: PostWastagePayload): Promise<PostWastageResult> => {
    const response = await api.post(`/admin/venue/${venueId}/inventory/waste`, payload);
    return response.data;
  },
};

export default wastageService;
